import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

type ThemeMode = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  setTheme: () => undefined,
});

const LIGHT_COLOR = "#ffffff";
const DARK_COLOR = "#0b1220";

function applyThemeClass(effectiveDark: boolean) {
  const root = document.documentElement;
  if (effectiveDark) root.classList.add("dark");
  else root.classList.remove("dark");

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", effectiveDark ? DARK_COLOR : LIGHT_COLOR);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    (localStorage.getItem("theme") as ThemeMode) || "system"
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => {
      const effectiveDark = theme === "system" ? media.matches : theme === "dark";
      applyThemeClass(effectiveDark);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeMode) => {
    localStorage.setItem("theme", mode);
    setThemeState(mode);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}








